import * as React from 'react';
import { Modal } from '@bytebrand/fe-ui-core-autobahn/ui';
import { fillFullGaranLabel, fillNestedGaranLabel, formatGaranYears, loadSvgText, GARAN_YOUR_EUROPE_URL } from './garanSvg';
import styles from './GaranLabel.styl';
const NESTED_LABEL_URL = require('media/images/garan/garan-label-nested.svg');
const FULL_LABEL_URL = require('media/images/garan/garan-label-colour.svg');
// Renders the official artwork inside a shadow root: its own <style> block (generic .cls-N class
// names, clip-path ids) must neither leak into the page nor collide with the second label.
// Fonts declared by the page (Inter) still apply inside the shadow tree. Exported — reused
// as-is by the checkout order-overview "10. Herstellergarantie" card (same official artwork,
// different card chrome/sizing), so both places stay pixel-identical to the annex SVG.
export const OfficialSvg = ({ url, fill, fillKey, className }) => {
    const hostRef = React.useRef(null);
    React.useEffect(() => {
        let cancelled = false;
        loadSvgText(url)
            .then((markup) => {
            const host = hostRef.current;
            if (cancelled || !host)
                return;
            const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
            root.innerHTML = `<style>svg{display:block;width:100%;height:auto}</style>${markup}`;
            const svg = root.querySelector('svg');
            // never show the artwork with its placeholders: an unfilled label is worse than none
            if (!svg || !fill(svg))
                root.innerHTML = '';
        })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [url, fillKey]);
    return React.createElement("span", { ref: hostRef, className: className, "aria-hidden": "true" });
};
// EU GARAN label on the VDP: the official nested display next to the offer; the full label opens
// on the first click. Shown ONLY for the backend's garanRequired result (car-basic-data
// services/garan) — no eligibility logic here.
const GaranLabel = ({ resolution, t }) => {
    const [open, setOpen] = React.useState(false);
    const { garanStatus, durationYears, producerName, modelIdentifier, termsUrl } = resolution || {};
    if (garanStatus !== 'garanRequired' || !(durationYears > 0) || !producerName || !modelIdentifier)
        return null;
    const values = { durationYears, producerName, modelIdentifier };
    const years = formatGaranYears(durationYears);
    const fillKey = [years, producerName, modelIdentifier].join('|');
    return (React.createElement("div", { className: styles.garan },
        React.createElement("button", { type: "button", className: styles.nested, "aria-label": t('vehicleProps:guarantee.garan.nestedAria', { years, producer: producerName }), "aria-haspopup": "dialog", onClick: () => setOpen(true) },
            React.createElement(OfficialSvg, { url: NESTED_LABEL_URL, fill: (svg) => fillNestedGaranLabel(svg, durationYears), fillKey: years, className: styles.nestedSvg })),
        React.createElement(Modal, { open: open, onClose: () => setOpen(false), title: t('vehicleProps:guarantee.garan.title'), size: "sm" },
            React.createElement("div", { className: styles.full },
                React.createElement("span", { role: "img", "aria-label": t('vehicleProps:guarantee.garan.fullAlt', { years, producer: producerName, model: modelIdentifier }) },
                    React.createElement(OfficialSvg, { url: FULL_LABEL_URL, fill: (svg) => fillFullGaranLabel(svg, values), fillKey: fillKey, className: styles.fullSvg })),
                React.createElement("a", { className: styles.link, href: GARAN_YOUR_EUROPE_URL, target: "_blank", rel: "noopener noreferrer" }, t('vehicleProps:guarantee.garan.moreInfo')),
                termsUrl && /^https?:\/\//i.test(termsUrl) ? (React.createElement("a", { className: styles.link, href: termsUrl, target: "_blank", rel: "noopener noreferrer" }, t('vehicleProps:guarantee.garan.terms'))) : null))));
};
export default GaranLabel;
